import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, formatMoney } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { AdCard } from '@/components/marketplace/AdCard';
import { SafeImage } from '@/components/marketplace/SafeImage';
import {
    Search, MapPin, ChevronRight, ChevronLeft, Star, Truck, Clock,
    ShoppingBag, ShoppingCart, User, LogOut, Building2, Plus,
    ArrowRight, Package, Zap, X, Home, Grid3x3, Navigation2,
    Check, TrendingUp, Flame
} from 'lucide-react';

// ─── DEFAULT APPS (shown until API loads) ─────────────────────────────────────

const DEFAULT_APPS = [
    { slug: 'okmarket',     name: 'OkMarket',     icon: '🛍️', color: '#dc2626', gradient_from: '#dc2626', gradient_to: '#991b1b', href: '/okgo',                   super_category_slug: '' },
    { slug: 'okfood',       name: 'OkFood',        icon: '🍔', color: '#ea580c', gradient_from: '#ea580c', gradient_to: '#9a3412', href: '/okgo?app=okfood',        super_category_slug: 'okfood' },
    { slug: 'okrestaurante',name: 'OkRestaurante', icon: '🍽️', color: '#1d4ed8', gradient_from: '#1d4ed8', gradient_to: '#1e3a8a', href: '/okgo?app=okrestaurante', super_category_slug: 'okrestaurante' },
    { slug: 'oktienda',     name: 'OkTienda',      icon: '🏪', color: '#16a34a', gradient_from: '#16a34a', gradient_to: '#14532d', href: '/okgo?app=oktienda',      super_category_slug: 'oktienda' },
    { slug: 'okfarmacia',   name: 'OkFarmacia',    icon: '💊', color: '#7c3aed', gradient_from: '#7c3aed', gradient_to: '#4c1d95', href: '/okgo?app=okfarmacia',    super_category_slug: 'okfarmacia' },
    { slug: 'okdrink',      name: 'OkDrink',       icon: '🍺', color: '#d97706', gradient_from: '#d97706', gradient_to: '#92400e', href: '/okgo?app=okdrink',       super_category_slug: 'okdrink' },
    { slug: 'oklovendo',    name: 'OkLoVendo',     icon: '📦', color: '#0891b2', gradient_from: '#0891b2', gradient_to: '#164e63', href: '/search',                 super_category_slug: 'oklovendo' },
];

// ─── helpers ──────────────────────────────────────────────────────────────────

function hexAlpha(hex, alpha) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
}

// ─── Location Modal ───────────────────────────────────────────────────────────

function LocationModal({ open, onClose, city, onSave }) {
    const [input, setInput] = useState(city || 'Ciudad de México');
    const [detecting, setDetecting] = useState(false);
    const [coords, setCoords] = useState(null);
    const [detected, setDetected] = useState(false);

    const detect = () => {
        if (!navigator.geolocation) { alert('Tu navegador no soporta geolocalización'); return; }
        setDetecting(true);
        setDetected(false);
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                const { latitude: lat, longitude: lng } = pos.coords;
                setCoords({ lat, lng });
                try {
                    const res = await fetch(
                        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
                        { headers: { 'Accept-Language': 'es' } }
                    );
                    const data = await res.json();
                    const city = data.address?.city || data.address?.town || data.address?.village || data.address?.state || 'Ubicación detectada';
                    setInput(city);
                } catch { setInput('Ubicación detectada'); }
                setDetected(true);
                setDetecting(false);
            },
            () => { setDetecting(false); }
        );
    };

    const mapSrc = coords
        ? `https://www.openstreetmap.org/export/embed.html?bbox=${coords.lng - 0.02},${coords.lat - 0.015},${coords.lng + 0.02},${coords.lat + 0.015}&layer=mapnik&marker=${coords.lat},${coords.lng}`
        : 'https://www.openstreetmap.org/export/embed.html?bbox=-99.25,19.30,-98.95,19.52&layer=mapnik';

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b">
                    <div className="flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-red-600" />
                        <h2 className="font-bold text-base">Tu ubicación</h2>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {/* Map */}
                <div className="relative h-48 bg-gray-100 overflow-hidden">
                    <iframe
                        key={coords ? `${coords.lat},${coords.lng}` : 'default'}
                        src={mapSrc}
                        title="Mapa"
                        className="w-full h-full border-0"
                        loading="lazy"
                        sandbox="allow-scripts allow-same-origin"
                    />
                    {detected && (
                        <div className="absolute top-2 right-2 flex items-center gap-1 bg-green-600 text-white text-[11px] font-bold px-2 py-1 rounded-full">
                            <Check className="h-3 w-3" /> Detectado
                        </div>
                    )}
                </div>

                {/* Form */}
                <div className="px-5 py-4 space-y-3">
                    <div>
                        <label className="text-xs font-semibold text-gray-600 block mb-1">Ciudad o colonia</label>
                        <input
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            className="w-full h-10 px-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 bg-gray-50"
                            placeholder="Ciudad de México, CDMX..."
                        />
                    </div>
                    <button
                        onClick={detect}
                        disabled={detecting}
                        className="w-full flex items-center justify-center gap-2 h-10 rounded-xl bg-gray-900 hover:bg-gray-800 text-white text-sm font-semibold transition-colors disabled:opacity-60"
                    >
                        <Navigation2 className="h-4 w-4" />
                        {detecting ? 'Detectando…' : 'Usar mi ubicación actual'}
                    </button>
                    <button
                        onClick={() => { onSave(input); onClose(); }}
                        className="w-full flex items-center justify-center gap-2 h-10 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-bold transition-colors"
                    >
                        Confirmar ubicación
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── App Strip (Grab-style environment selector) ──────────────────────────────

function AppStrip({ apps, activeApp, onSelect }) {
    const ref = useRef(null);

    // Scroll active app into view
    useEffect(() => {
        if (!ref.current || !activeApp) return;
        const el = ref.current.querySelector(`[data-app="${activeApp.slug}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }, [activeApp]);

    return (
        <div className="bg-white border-b border-gray-100">
            {/* Active app banner */}
            {activeApp && (
                <div
                    className="relative overflow-hidden"
                    style={{ background: `linear-gradient(135deg, ${activeApp.gradient_from}, ${activeApp.gradient_to})` }}
                >
                    <div className="absolute inset-0 opacity-10"
                        style={{ backgroundImage: 'radial-gradient(circle at 80% 50%, white 0%, transparent 60%)' }} />
                    <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-4 relative z-10">
                        <div className="h-14 w-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0 shadow-lg">
                            {activeApp.icon_image_url
                                ? <img src={activeApp.icon_image_url} alt={activeApp.name} className="h-9 w-9 object-contain" />
                                : <span className="text-3xl leading-none">{activeApp.icon || '🛍️'}</span>
                            }
                        </div>
                        <div className="flex-1 min-w-0">
                            <h2 className="text-white font-black text-lg leading-tight">{activeApp.name}</h2>
                            <p className="text-white/80 text-xs">{activeApp.tagline || activeApp.description}</p>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                            {activeApp.href && (
                                <Link
                                    to={activeApp.external ? undefined : activeApp.href}
                                    href={activeApp.external ? activeApp.href : undefined}
                                    target={activeApp.external ? '_blank' : undefined}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-white/20 hover:bg-white/30 text-white text-xs font-bold rounded-full transition-colors border border-white/30"
                                >
                                    Explorar <ArrowRight className="h-3 w-3" />
                                </Link>
                            )}
                            <button
                                onClick={() => onSelect(null)}
                                className="p-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
                                title="Ver todo"
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* App icons row */}
            <div className="max-w-6xl mx-auto px-2 sm:px-4">
                <div ref={ref} className="flex gap-1 overflow-x-auto scrollbar-hide py-3">
                    {apps.map(app => {
                        const isActive = activeApp?.slug === app.slug;
                        return (
                            <button
                                key={app.slug}
                                data-app={app.slug}
                                onClick={() => onSelect(isActive ? null : app)}
                                className={`flex-shrink-0 flex flex-col items-center gap-1.5 px-3 py-2 rounded-2xl transition-all duration-200 ${
                                    isActive ? 'bg-gray-50 scale-105' : 'hover:bg-gray-50'
                                }`}
                            >
                                {/* Icon */}
                                <div
                                    className={`h-[84px] w-[84px] rounded-2xl flex items-center justify-center transition-all duration-200 shadow-sm ${
                                        isActive ? 'shadow-lg scale-105' : ''
                                    }`}
                                    style={{
                                        background: isActive
                                            ? `linear-gradient(135deg, ${app.gradient_from}, ${app.gradient_to})`
                                            : `linear-gradient(135deg, ${hexAlpha(app.gradient_from, 0.12)}, ${hexAlpha(app.gradient_to, 0.12)})`,
                                        boxShadow: isActive ? `0 4px 16px ${hexAlpha(app.color, 0.4)}` : undefined,
                                        outline: isActive ? `2.5px solid ${app.color}` : '2.5px solid transparent',
                                        outlineOffset: '2px',
                                    }}
                                >
                                    {app.icon_image_url ? (
                                        <img src={app.icon_image_url} alt={app.name}
                                            className={`object-contain transition-all ${isActive ? 'h-12 w-12' : 'h-11 w-11 opacity-70'}`} />
                                    ) : (
                                        <span className={`leading-none transition-all ${isActive ? 'text-4xl' : 'text-3xl opacity-75'}`}>
                                            {app.icon || '🛍️'}
                                        </span>
                                    )}
                                </div>
                                {/* Name — always show */}
                                <span
                                    className={`text-[13px] font-semibold whitespace-nowrap transition-colors leading-tight text-center max-w-[90px] truncate ${
                                        isActive ? 'font-black' : 'text-gray-500'
                                    }`}
                                    style={{ color: isActive ? app.color : undefined }}
                                >
                                    {app.name}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// ─── Category tiles (big square, Grab-style) ──────────────────────────────────

function CategoryTiles({ superCats, activeFilter, onSelect }) {
    if (!superCats.length) return null;
    return (
        <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-1 px-0.5">
            {/* "Todos" pill */}
            <button
                onClick={() => onSelect('')}
                className={`flex-shrink-0 flex flex-col items-center gap-1.5 group`}
            >
                <div className={`h-20 w-20 rounded-2xl flex items-center justify-center transition-all shadow-sm group-hover:shadow-md ${
                    !activeFilter ? 'bg-gray-900 shadow-lg scale-105' : 'bg-gray-100 group-hover:bg-gray-200'
                }`}>
                    <Grid3x3 className={`h-8 w-8 ${!activeFilter ? 'text-white' : 'text-gray-500'}`} />
                </div>
                <span className={`text-[11px] font-bold whitespace-nowrap ${!activeFilter ? 'text-gray-900' : 'text-gray-500'}`}>Todo</span>
            </button>

            {superCats.map(cat => {
                const isActive = activeFilter === cat.slug;
                return (
                    <button
                        key={cat.slug}
                        onClick={() => onSelect(isActive ? '' : cat.slug)}
                        className="flex-shrink-0 flex flex-col items-center gap-1.5 group"
                    >
                        <div
                            className={`h-20 w-20 rounded-2xl flex items-center justify-center transition-all shadow-sm group-hover:shadow-md overflow-hidden ${
                                isActive ? 'scale-110 shadow-lg' : 'group-hover:scale-105'
                            }`}
                            style={{
                                background: `linear-gradient(145deg, ${cat.gradient_from}${isActive ? '' : '99'}, ${cat.gradient_to}${isActive ? '' : '88'})`,
                                boxShadow: isActive ? `0 6px 20px ${hexAlpha(cat.color, 0.4)}` : undefined,
                                outline: isActive ? `2px solid ${cat.color}` : 'none',
                                outlineOffset: '2px',
                            }}
                        >
                            {cat.banner_image_url || cat.icon_image_url ? (
                                <img src={cat.banner_image_url || cat.icon_image_url} alt={cat.name}
                                    className="w-full h-full object-cover" />
                            ) : (
                                <span className="text-3xl leading-none select-none">{cat.icon || '🏷️'}</span>
                            )}
                        </div>
                        <span
                            className={`text-[11px] font-semibold whitespace-nowrap leading-tight text-center max-w-[80px] truncate transition-colors ${
                                isActive ? 'font-black' : 'text-gray-600'
                            }`}
                            style={{ color: isActive ? cat.color : undefined }}
                        >
                            {cat.name}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

// ─── Super-cat destination cards (same 4:5 ratio as AdCard) ──────────────────

function SuperCatCard({ cat, onClick }) {
    return (
        <button
            onClick={onClick}
            className="flex-shrink-0 w-36 sm:w-40 rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-200 snap-start group bg-white text-left"
        >
            {/* Cover: same 4:5 ratio as AdCard */}
            <div className="relative aspect-[4/5] overflow-hidden">
                {cat.banner_image_url ? (
                    <img src={cat.banner_image_url} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                ) : (
                    <div
                        className="w-full h-full flex flex-col items-center justify-center gap-2"
                        style={{ background: `linear-gradient(145deg, ${cat.gradient_from}, ${cat.gradient_to})` }}
                    >
                        {cat.icon_image_url ? (
                            <img src={cat.icon_image_url} alt={cat.name} className="h-14 w-14 object-contain" />
                        ) : (
                            <span className="text-5xl leading-none select-none">{cat.icon || '🏷️'}</span>
                        )}
                    </div>
                )}
                {/* Overlay */}
                <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-2.5">
                    <p className="text-white font-black text-sm leading-tight">{cat.name}</p>
                    {cat.tagline && (
                        <p className="text-white/75 text-[10px] leading-tight line-clamp-1 mt-0.5">{cat.tagline}</p>
                    )}
                </div>
            </div>
        </button>
    );
}

// ─── Business card (Rappi-style) ──────────────────────────────────────────────

function BusinessCard({ biz, superCats }) {
    const cat = superCats.find(c => c.slug === biz.super_category);
    return (
        <Link
            to={`/aliado/${biz.slug}`}
            className="flex-shrink-0 w-56 sm:w-64 rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-200 snap-start group"
        >
            {/* Cover */}
            <div className="relative h-36 bg-gray-100 overflow-hidden">
                {biz.cover_url ? (
                    <SafeImage src={biz.cover_url} alt={biz.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center"
                        style={{ background: `linear-gradient(135deg, ${cat?.gradient_from || '#dc2626'}33, ${cat?.gradient_to || '#991b1b'}55)` }}>
                        <span className="text-5xl">{cat?.icon || '🏪'}</span>
                    </div>
                )}
                <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-black/50 to-transparent" />
                {/* Logo */}
                <div className="absolute bottom-2 left-3 h-10 w-10 rounded-xl bg-white shadow-lg border-2 border-white flex items-center justify-center overflow-hidden">
                    {biz.logo_url
                        ? <SafeImage src={biz.logo_url} alt={biz.name} className="w-full h-full object-cover" />
                        : <span className="text-lg">{cat?.icon || '🏪'}</span>
                    }
                </div>
                {biz.verified && (
                    <div className="absolute top-2 right-2">
                        <span className="inline-flex items-center gap-0.5 bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">✓ Ver.</span>
                    </div>
                )}
            </div>
            {/* Info */}
            <div className="p-3">
                <p className="font-bold text-sm text-gray-900 line-clamp-1">{biz.name}</p>
                <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{biz.tagline || biz.city}</p>
                <div className="flex items-center gap-3 mt-2">
                    {biz.rating_avg > 0 && (
                        <div className="flex items-center gap-0.5">
                            <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                            <span className="text-xs font-bold text-gray-800">{biz.rating_avg.toFixed(1)}</span>
                        </div>
                    )}
                    {biz.delivery_available && (
                        <div className="flex items-center gap-0.5 text-gray-400">
                            <Truck className="h-3 w-3" />
                            <span className="text-xs">{biz.delivery_cost === 0 ? 'Gratis' : formatMoney(biz.delivery_cost)}</span>
                        </div>
                    )}
                    {biz.estimated_time && (
                        <div className="flex items-center gap-0.5 text-gray-400">
                            <Clock className="h-3 w-3" />
                            <span className="text-xs">{biz.estimated_time} min</span>
                        </div>
                    )}
                </div>
            </div>
        </Link>
    );
}

// ─── Horizontal scroll with arrows ───────────────────────────────────────────

function HScroll({ children, className = '' }) {
    const ref = useRef(null);
    const scroll = dir => ref.current?.scrollBy({ left: dir * 300, behavior: 'smooth' });
    return (
        <div className={`relative group ${className}`}>
            <button onClick={() => scroll(-1)}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-white shadow-md border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity -translate-x-4">
                <ChevronLeft className="h-4 w-4 text-gray-600" />
            </button>
            <div ref={ref} className="flex gap-3 overflow-x-auto scrollbar-hide pb-2 snap-x snap-mandatory">
                {children}
            </div>
            <button onClick={() => scroll(1)}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-white shadow-md border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity translate-x-4">
                <ChevronRight className="h-4 w-4 text-gray-600" />
            </button>
        </div>
    );
}

// ─── Section Header ───────────────────────────────────────────────────────────

function SectionHeader({ title, sub, linkLabel, linkTo, icon: Icon, badge, accentColor }) {
    return (
        <div className="flex items-end justify-between mb-4">
            <div>
                <div className="flex items-center gap-2 mb-0.5">
                    {Icon && <Icon className="h-5 w-5" style={{ color: accentColor || '#dc2626' }} />}
                    <h2 className="font-black text-lg sm:text-xl text-gray-900">{title}</h2>
                    {badge && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 uppercase tracking-wide">{badge}</span>
                    )}
                </div>
                {sub && <p className="text-xs text-gray-400">{sub}</p>}
            </div>
            {linkTo && (
                <Link to={linkTo} className="flex items-center gap-0.5 text-sm font-semibold flex-shrink-0 ml-4 hover:opacity-80 transition-opacity"
                    style={{ color: accentColor || '#dc2626' }}>
                    {linkLabel || 'Ver todos'} <ChevronRight className="h-4 w-4" />
                </Link>
            )}
        </div>
    );
}

// ─── OkGo Header ─────────────────────────────────────────────────────────────

function OkGoHeader({ onSearch, searchValue, setSearchValue, city, onLocationClick }) {
    const { role, user, logout } = useAuth();
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);

    const submit = (e) => { e.preventDefault(); onSearch(searchValue); };

    return (
        <header className="sticky top-0 z-50 bg-white/98 backdrop-blur-md border-b border-gray-100 shadow-sm">
            <div className="max-w-6xl mx-auto px-4 h-15 py-2.5 flex items-center gap-3">

                {/* Logo — text only, no icon square */}
                <Link to="/okgo" className="flex-shrink-0 flex items-center gap-0 group">
                    <span className="font-black text-2xl tracking-tight">
                        <span className="text-gray-900">ok</span>
                        <span className="text-red-600">go</span>
                    </span>
                </Link>

                {/* Location button */}
                <button
                    onClick={onLocationClick}
                    className="hidden sm:flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-red-600 transition-colors bg-gray-50 hover:bg-red-50 border border-gray-200 hover:border-red-200 rounded-full px-3 py-1.5 flex-shrink-0 max-w-[140px]"
                >
                    <MapPin className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
                    <span className="truncate">{city || 'Ubicación'}</span>
                </button>

                {/* Search */}
                <form onSubmit={submit} className="flex-1">
                    <div className="relative">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            value={searchValue}
                            onChange={e => setSearchValue(e.target.value)}
                            placeholder="Restaurantes, productos, tiendas…"
                            className="w-full pl-10 pr-16 h-10 rounded-full border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent focus:bg-white transition-all placeholder:text-gray-400"
                        />
                        <button type="submit"
                            className="absolute right-1 top-1/2 -translate-y-1/2 h-8 px-4 bg-red-600 hover:bg-red-700 text-white font-bold text-xs rounded-full transition-colors">
                            go
                        </button>
                    </div>
                </form>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                    {role === 'user' ? (
                        <div className="relative">
                            <button onClick={() => setMenuOpen(!menuOpen)}
                                className="flex items-center gap-2 rounded-full border border-gray-200 px-2.5 py-1.5 hover:bg-gray-50 transition-colors">
                                <div className="h-6 w-6 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center text-white text-[10px] font-black">
                                    {user?.nickname?.[0]?.toUpperCase() || 'U'}
                                </div>
                                <span className="hidden sm:inline text-xs font-semibold text-gray-800 max-w-[72px] truncate">{user?.nickname}</span>
                            </button>
                            {menuOpen && (
                                <div className="absolute right-0 top-12 w-52 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 py-2 overflow-hidden">
                                    <div className="px-4 py-2 border-b mb-1">
                                        <p className="text-xs text-gray-400">Conectado como</p>
                                        <p className="text-sm font-bold truncate">{user?.nickname}</p>
                                    </div>
                                    <Link to="/dashboard" className="flex items-center gap-2.5 px-4 py-2.5 hover:bg-gray-50 text-sm text-gray-700" onClick={() => setMenuOpen(false)}>
                                        <User className="h-4 w-4 text-gray-400" /> Mi cuenta
                                    </Link>
                                    <Link to="/carrito" className="flex items-center gap-2.5 px-4 py-2.5 hover:bg-gray-50 text-sm text-gray-700" onClick={() => setMenuOpen(false)}>
                                        <ShoppingCart className="h-4 w-4 text-gray-400" /> Mi carrito
                                    </Link>
                                    <Link to="/aliado" className="flex items-center gap-2.5 px-4 py-2.5 hover:bg-gray-50 text-sm text-gray-700" onClick={() => setMenuOpen(false)}>
                                        <Building2 className="h-4 w-4 text-gray-400" /> Mi negocio
                                    </Link>
                                    <Link to="/dashboard/wallet" className="flex items-center gap-2.5 px-4 py-2.5 hover:bg-gray-50 text-sm text-gray-700" onClick={() => setMenuOpen(false)}>
                                        <Zap className="h-4 w-4 text-gray-400" /> OkWallet
                                    </Link>
                                    <hr className="my-1 border-gray-100" />
                                    <button onClick={() => { logout(); setMenuOpen(false); }}
                                        className="w-full flex items-center gap-2.5 px-4 py-2.5 hover:bg-red-50 text-sm text-red-600">
                                        <LogOut className="h-4 w-4" /> Cerrar sesión
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex items-center gap-2">
                            <button onClick={() => navigate('/acceso')}
                                className="h-9 px-4 bg-red-600 hover:bg-red-700 text-white font-bold text-sm rounded-full transition-colors shadow-sm">
                                Ingresar
                            </button>
                            <Link to="/aliado"
                                className="hidden md:flex h-9 px-4 border border-red-200 text-red-600 hover:bg-red-50 font-semibold text-sm rounded-full transition-colors items-center gap-1.5">
                                <Building2 className="h-3.5 w-3.5" /> Mi negocio
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
}

// ─── Hero (changes per selected app) ─────────────────────────────────────────

function AppHero({ activeApp, defaultBanners }) {
    const [idx, setIdx] = useState(0);
    const timerRef = useRef(null);

    const slides = defaultBanners?.length ? defaultBanners : [
        { title: 'Pide lo que quieras', sub: 'Miles de negocios locales a un toque de distancia', cta: 'Explorar ahora', ctaLink: '/search', emoji: '🚀', color_from: '#dc2626', color_to: '#991b1b' },
        { title: 'Tu OkNegocio, en línea', sub: 'Regístrate gratis y empieza a recibir pedidos', cta: 'Registrar negocio', ctaLink: '/aliado', emoji: '🏪', color_from: '#7c3aed', color_to: '#4c1d95' },
        { title: 'OkWallet · Sin efectivo', sub: 'Recarga tu saldo y paga de forma segura', cta: 'Recargar', ctaLink: '/dashboard/wallet', emoji: '💳', color_from: '#0891b2', color_to: '#164e63' },
    ];

    useEffect(() => {
        if (activeApp) { clearInterval(timerRef.current); return; }
        timerRef.current = setInterval(() => setIdx(p => (p + 1) % slides.length), 4500);
        return () => clearInterval(timerRef.current);
    }, [activeApp, slides.length]);

    // When an app is selected, show app-specific hero
    if (activeApp) {
        return (
            <div
                className="relative overflow-hidden"
                style={{ background: `linear-gradient(135deg, ${activeApp.gradient_from}18, ${activeApp.gradient_to}08)` }}
            >
                <div className="max-w-6xl mx-auto px-4 py-8 flex items-center gap-6">
                    <div className="flex-shrink-0 hidden sm:flex">
                        <div className="h-24 w-24 rounded-3xl flex items-center justify-center shadow-xl"
                            style={{ background: `linear-gradient(135deg, ${activeApp.gradient_from}, ${activeApp.gradient_to})` }}>
                            {activeApp.icon_image_url
                                ? <img src={activeApp.icon_image_url} alt={activeApp.name} className="h-14 w-14 object-contain" />
                                : <span className="text-5xl leading-none">{activeApp.icon || '🛍️'}</span>
                            }
                        </div>
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold uppercase tracking-widest mb-1 opacity-60">OkGo</p>
                        <h1 className="text-2xl sm:text-3xl font-black text-gray-900 leading-tight mb-1">{activeApp.name}</h1>
                        <p className="text-gray-500 text-sm mb-4 max-w-md">{activeApp.tagline || activeApp.description}</p>
                        {activeApp.href && (
                            <Link to={activeApp.external ? undefined : activeApp.href}
                                href={activeApp.external ? activeApp.href : undefined}
                                target={activeApp.external ? '_blank' : undefined}>
                                <button
                                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-bold text-sm shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                                    style={{ background: `linear-gradient(135deg, ${activeApp.gradient_from}, ${activeApp.gradient_to})` }}
                                >
                                    Ir a {activeApp.name} <ArrowRight className="h-4 w-4" />
                                </button>
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Default: rotating carousel
    const slide = slides[idx];
    const from = slide.color_from || slide.color || '#dc2626';
    const to   = slide.color_to   || slide.color || '#991b1b';
    return (
        <div className="relative overflow-hidden transition-all duration-700"
            style={{ background: `linear-gradient(135deg, ${from}, ${to})` }}>
            <div className="absolute -top-16 -right-16 h-64 w-64 rounded-full bg-white/10 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-10 -left-10 h-48 w-48 rounded-full bg-black/10 blur-2xl pointer-events-none" />
            <div className="max-w-6xl mx-auto px-4 py-10 sm:py-14 flex items-center gap-6 relative z-10">
                <div className="flex-1 min-w-0">
                    <p className="text-white/70 text-xs font-bold uppercase tracking-widest mb-2">OkGo · La super app local</p>
                    <h1 className="text-3xl sm:text-4xl font-black text-white leading-tight mb-3">{slide.title}</h1>
                    <p className="text-white/80 text-sm mb-6 max-w-md">{slide.sub}</p>
                    <Link to={slide.ctaLink || slide.link || '/okgo'}>
                        <button className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-gray-900 font-bold text-sm shadow-lg hover:shadow-xl hover:scale-105 transition-all">
                            {slide.cta} <ArrowRight className="h-4 w-4" />
                        </button>
                    </Link>
                </div>
                <div className="flex-shrink-0 hidden sm:flex text-[80px] sm:text-[96px] select-none drop-shadow-2xl">
                    {slide.emoji || slide.icon || '🚀'}
                </div>
            </div>
            {/* Dots */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                {slides.map((_, i) => (
                    <button key={i} onClick={() => setIdx(i)}
                        className={`rounded-full transition-all ${i === idx ? 'w-5 h-2 bg-white' : 'w-2 h-2 bg-white/40'}`} />
                ))}
            </div>
        </div>
    );
}

// ─── Mobile bottom nav ────────────────────────────────────────────────────────

function MobileBottomNav() {
    const { role } = useAuth();
    const navigate = useNavigate();
    return (
        <nav className="fixed bottom-0 inset-x-0 z-50 bg-white/95 backdrop-blur-md border-t border-gray-100 flex sm:hidden">
            <Link to="/okgo"   className="flex-1 flex flex-col items-center gap-0.5 py-3 text-red-600">
                <Home className="h-5 w-5" /><span className="text-[10px] font-semibold">Inicio</span>
            </Link>
            <Link to="/search" className="flex-1 flex flex-col items-center gap-0.5 py-3 text-gray-400 hover:text-gray-600">
                <Search className="h-5 w-5" /><span className="text-[10px] font-semibold">Buscar</span>
            </Link>
            <Link to="/carrito" className="flex-1 flex flex-col items-center gap-0.5 py-3 text-gray-400 hover:text-gray-600">
                <ShoppingCart className="h-5 w-5" /><span className="text-[10px] font-semibold">Carrito</span>
            </Link>
            {role === 'user' ? (
                <Link to="/dashboard" className="flex-1 flex flex-col items-center gap-0.5 py-3 text-gray-400 hover:text-gray-600">
                    <User className="h-5 w-5" /><span className="text-[10px] font-semibold">Mi cuenta</span>
                </Link>
            ) : (
                <button onClick={() => navigate('/acceso')} className="flex-1 flex flex-col items-center gap-0.5 py-3 text-gray-400">
                    <User className="h-5 w-5" /><span className="text-[10px] font-semibold">Ingresar</span>
                </button>
            )}
        </nav>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function OkGoHomePage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const activeAppParam = searchParams.get('app') || searchParams.get('cat') || '';

    const [searchValue, setSearchValue]     = useState('');
    const [platformApps, setPlatformApps]   = useState(DEFAULT_APPS);
    const [activeApp, setActiveApp]         = useState(null);
    const [activeFilter, setActiveFilter]   = useState(''); // super_category_slug filter
    const [superCats, setSuperCats]         = useState([]);
    const [businesses, setBusinesses]       = useState([]);
    const [ads, setAds]                     = useState([]);
    const [systemBanners, setSystemBanners] = useState([]);
    const [loading, setLoading]             = useState(true);
    const [locationModal, setLocationModal] = useState(false);
    const [city, setCity]                   = useState(() => localStorage.getItem('okgo_city') || 'Ciudad de México');

    // Initialize active app from URL
    useEffect(() => {
        if (activeAppParam) {
            const found = platformApps.find(a => a.slug === activeAppParam);
            if (found) {
                setActiveApp(found);
                setActiveFilter(found.super_category_slug || '');
            }
        } else {
            setActiveApp(null);
            setActiveFilter('');
        }
    }, [activeAppParam, platformApps]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [appsRes, catRes, homeRes, adsRes] = await Promise.allSettled([
                api.get('/platform-apps').catch(() => ({ data: DEFAULT_APPS })),
                api.get('/super-categories'),
                api.get('/okgo/home', { params: activeFilter ? { super_category: activeFilter } : {} }),
                api.get('/ads', { params: { limit: 16, sort: 'recent' } }),
            ]);
            if (appsRes.status === 'fulfilled' && appsRes.value.data?.length) {
                setPlatformApps(appsRes.value.data);
            }
            if (catRes.status === 'fulfilled') setSuperCats(catRes.value.data || []);
            if (homeRes.status === 'fulfilled') {
                setBusinesses(homeRes.value.data.featured_businesses || []);
                if (homeRes.value.data.system_banners?.length) setSystemBanners(homeRes.value.data.system_banners);
            }
            if (adsRes.status === 'fulfilled') {
                const raw = adsRes.value.data;
                setAds(Array.isArray(raw?.items) ? raw.items : Array.isArray(raw?.ads) ? raw.ads : Array.isArray(raw) ? raw : []);
            }
        } catch { /* silent */ }
        finally { setLoading(false); }
    }, [activeFilter]);

    useEffect(() => { load(); }, [load]);

    const handleSearch = (q) => {
        if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
        else navigate('/search');
    };

    const handleAppSelect = (app) => {
        if (!app) {
            setActiveApp(null);
            setActiveFilter('');
            navigate('/okgo', { replace: true });
        } else {
            setActiveApp(app);
            setActiveFilter(app.super_category_slug || '');
            navigate(`/okgo?app=${app.slug}`, { replace: true });
        }
    };

    const handleCatFilter = (slug) => {
        setActiveFilter(slug === activeFilter ? '' : slug);
    };

    const handleSaveCity = (c) => {
        setCity(c);
        localStorage.setItem('okgo_city', c);
    };

    const themeColor = activeApp?.color || '#dc2626';
    const activeCatInfo = superCats.find(c => c.slug === activeFilter);

    return (
        <div className="min-h-screen bg-[#f8f9fa] pb-16 sm:pb-0">
            {/* Header */}
            <OkGoHeader
                onSearch={handleSearch}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                city={city}
                onLocationClick={() => setLocationModal(true)}
            />

            {/* Location Modal */}
            <LocationModal
                open={locationModal}
                onClose={() => setLocationModal(false)}
                city={city}
                onSave={handleSaveCity}
            />

            {/* App Strip — Grab-style environment selector */}
            <AppStrip
                apps={platformApps}
                activeApp={activeApp}
                onSelect={handleAppSelect}
            />

            {/* App Hero */}
            <AppHero activeApp={activeApp} defaultBanners={systemBanners} />

            {/* Main content */}
            <main className="max-w-6xl mx-auto px-4 py-6 space-y-10">

                {/* Category tiles */}
                <section>
                    <SectionHeader
                        title="Explora por categoría"
                        sub="Elige tu categoría y descubre lo mejor"
                        icon={Grid3x3}
                        accentColor={themeColor}
                    />
                    {loading ? (
                        <div className="flex gap-3 pb-1">
                            {[...Array(7)].map((_, i) => (
                                <div key={i} className="flex-shrink-0 w-20 h-28 rounded-2xl bg-gray-200 animate-pulse" />
                            ))}
                        </div>
                    ) : (
                        <CategoryTiles
                            superCats={superCats}
                            activeFilter={activeFilter}
                            onSelect={handleCatFilter}
                        />
                    )}
                </section>

                {/* Businesses */}
                <section>
                    <SectionHeader
                        title={activeCatInfo ? `${activeCatInfo.name} cerca de ti` : 'Negocios cerca de ti'}
                        sub={activeCatInfo?.tagline || 'Los mejores negocios locales'}
                        icon={TrendingUp}
                        badge="Destacados"
                        linkTo="/okgo"
                        linkLabel="Ver todos"
                        accentColor={themeColor}
                    />
                    {loading ? (
                        <div className="flex gap-3">
                            {[...Array(4)].map((_, i) => (
                                <div key={i} className="flex-shrink-0 w-60 h-52 rounded-2xl bg-gray-200 animate-pulse" />
                            ))}
                        </div>
                    ) : businesses.length === 0 ? (
                        <div className="text-center py-12 bg-white rounded-3xl border border-dashed border-gray-200">
                            <Building2 className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                            <p className="font-semibold text-gray-600 mb-1">
                                {activeFilter ? `Sin negocios de ${activeCatInfo?.name || activeFilter} aún` : 'Sin negocios disponibles aún'}
                            </p>
                            <p className="text-sm text-gray-400 mb-4">¡Sé el primero en registrar tu negocio!</p>
                            <Link to="/aliado">
                                <button className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-bold text-sm rounded-full transition-colors">
                                    <Plus className="h-4 w-4" /> Registrar mi negocio
                                </button>
                            </Link>
                        </div>
                    ) : (
                        <HScroll>
                            {businesses.map(biz => <BusinessCard key={biz.id} biz={biz} superCats={superCats} />)}
                        </HScroll>
                    )}
                </section>

                {/* Super categories as destination cards */}
                {superCats.length > 0 && (
                    <section>
                        <SectionHeader
                            title="Descubre más categorías"
                            sub="Todo lo que OkGo tiene para ti"
                            icon={ShoppingBag}
                            accentColor={themeColor}
                        />
                        <HScroll>
                            {superCats.map(cat => (
                                <SuperCatCard key={cat.slug} cat={cat} onClick={() => handleCatFilter(cat.slug)} />
                            ))}
                        </HScroll>
                    </section>
                )}

                {/* Mid-page CTA cards */}
                <section>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Link to="/aliado" className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900 to-gray-800 p-5 flex items-center gap-4 hover:shadow-xl transition-shadow">
                            <div className="flex-1 min-w-0">
                                <p className="text-red-400 text-xs font-bold mb-1">OkNegocios</p>
                                <p className="text-white font-black text-lg leading-tight">Registra tu negocio gratis</p>
                                <p className="text-gray-400 text-xs mt-1">Empieza a recibir pedidos hoy</p>
                            </div>
                            <span className="text-4xl flex-shrink-0">🏪</span>
                            <div className="absolute -right-4 -bottom-4 h-24 w-24 rounded-full bg-red-600/20 blur-2xl" />
                        </Link>
                        <Link to="/dashboard/wallet" className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-700 to-violet-900 p-5 flex items-center gap-4 hover:shadow-xl transition-shadow">
                            <div className="flex-1 min-w-0">
                                <p className="text-violet-300 text-xs font-bold mb-1">OkWallet</p>
                                <p className="text-white font-black text-lg leading-tight">Paga sin efectivo</p>
                                <p className="text-violet-300/70 text-xs mt-1">Tu saldo, siempre contigo</p>
                            </div>
                            <span className="text-4xl flex-shrink-0">💳</span>
                            <div className="absolute -right-4 -bottom-4 h-24 w-24 rounded-full bg-white/10 blur-2xl" />
                        </Link>
                    </div>
                </section>

                {/* OkLoVendo ads */}
                {(!activeFilter || activeFilter === 'oklovendo') && (
                    <section>
                        <SectionHeader
                            title="Últimos anuncios"
                            sub="OkLoVendo · Marketplace de productos locales"
                            icon={Flame}
                            badge="Nuevo"
                            linkTo="/search"
                            linkLabel="Ver todos"
                            accentColor={themeColor}
                        />
                        {loading ? (
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                {[...Array(8)].map((_, i) => <div key={i} className="aspect-[4/5] rounded-2xl bg-gray-200 animate-pulse" />)}
                            </div>
                        ) : ads.length > 0 ? (
                            <>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                    {ads.slice(0, 8).map(ad => <AdCard key={ad.id} ad={ad} />)}
                                </div>
                                {ads.length > 8 && (
                                    <div className="text-center mt-6">
                                        <Link to="/search">
                                            <button className="inline-flex items-center gap-2 px-6 py-3 border-2 border-gray-200 hover:border-red-300 hover:text-red-600 text-gray-700 font-bold text-sm rounded-full transition-all">
                                                Ver más anuncios <ArrowRight className="h-4 w-4" />
                                            </button>
                                        </Link>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="text-center py-12 bg-white rounded-3xl border border-dashed border-gray-200">
                                <Package className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                                <p className="font-semibold text-gray-600 mb-1">Sin anuncios por ahora</p>
                                <p className="text-sm text-gray-400 mb-4">Sé el primero en publicar un producto</p>
                                <Link to="/dashboard/ad/new">
                                    <button className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-bold text-sm rounded-full transition-colors">
                                        <Plus className="h-4 w-4" /> Publicar anuncio
                                    </button>
                                </Link>
                            </div>
                        )}
                    </section>
                )}

                {/* Final CTA */}
                <section>
                    <div className="relative overflow-hidden rounded-3xl bg-gray-950 p-7 sm:p-10">
                        <div className="absolute inset-0 opacity-[0.04]"
                            style={{ backgroundImage: 'repeating-linear-gradient(0deg,#fff 0px,#fff 1px,transparent 1px,transparent 40px),repeating-linear-gradient(90deg,#fff 0px,#fff 1px,transparent 1px,transparent 40px)' }} />
                        <div className="absolute -right-12 -top-12 h-64 w-64 rounded-full bg-red-700/20 blur-3xl" />
                        <div className="absolute -left-8 -bottom-8 h-48 w-48 rounded-full bg-violet-700/20 blur-3xl" />
                        <div className="relative z-10 flex flex-col sm:flex-row items-center gap-6">
                            <div className="flex-1 text-center sm:text-left">
                                <div className="inline-flex items-center gap-1.5 bg-red-600/20 border border-red-500/30 text-red-400 text-xs font-bold px-3 py-1 rounded-full mb-4">
                                    <Zap className="h-3 w-3" /> Únete · Es gratis
                                </div>
                                <h2 className="text-2xl sm:text-3xl font-black text-white mb-2 leading-tight">Lleva tu negocio al mundo digital</h2>
                                <p className="text-gray-400 text-sm mb-5 max-w-md">Restaurantes, tiendas, farmacias y más. Crea tu perfil y empieza a recibir pedidos hoy.</p>
                                <div className="flex flex-wrap gap-3 justify-center sm:justify-start">
                                    <Link to="/aliado">
                                        <button className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-bold text-sm rounded-full transition-colors shadow-lg">
                                            <Building2 className="h-4 w-4" /> Registrar mi negocio
                                        </button>
                                    </Link>
                                    <Link to="/search">
                                        <button className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 text-white hover:bg-white/10 font-semibold text-sm rounded-full transition-colors">
                                            <ShoppingBag className="h-4 w-4" /> Explorar marketplace
                                        </button>
                                    </Link>
                                </div>
                            </div>
                            <div className="text-[80px] sm:text-[96px] select-none flex-shrink-0">🏪</div>
                        </div>
                    </div>
                </section>
            </main>

            {/* Footer */}
            <footer className="mt-4 bg-white border-t border-gray-100 py-10">
                <div className="max-w-6xl mx-auto px-4">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                        <div>
                            <div className="flex items-center gap-1 mb-2">
                                <span className="font-black text-xl">
                                    <span className="text-gray-900">ok</span><span className="text-red-600">go</span>
                                </span>
                            </div>
                            <p className="text-xs text-gray-400">La super app del mercado local mexicano</p>
                            <p className="text-xs text-gray-300 mt-0.5">© 2026 OkGo · Todos los derechos reservados</p>
                        </div>
                        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-500">
                            <Link to="/search"  className="hover:text-red-600 font-medium transition-colors">OkLoVendo</Link>
                            <Link to="/aliado"  className="hover:text-red-600 font-medium transition-colors">OkNegocios</Link>
                            <Link to="/acceso"  className="hover:text-red-600 font-medium transition-colors">Ingresar</Link>
                            <Link to="/planes"  className="hover:text-red-600 font-medium transition-colors">Planes</Link>
                            <Link to="/okgo"    className="hover:text-red-600 font-medium transition-colors">OkGo Home</Link>
                        </div>
                    </div>
                </div>
            </footer>

            <MobileBottomNav />
        </div>
    );
}
